import { createEffect, untrack } from "solid-js";
import { fromActorRef } from "@fleet/state-machines/solid";
import { sessionActor } from "@/lib/session-machine";
import { useQuery } from "@tanstack/solid-query";
import { queryKeys } from "@malla/shared";
import { cloneSession } from "@/lib/auth";
import { readActiveAccountSync } from "@/lib/accounts";
import { api } from "@/lib/api";
import { SESSION_STALE_TIME_MS } from "@/lib/constants";

interface AppError {
  message?: string;
  status?: number;
}

function isAppError(error: unknown): error is AppError {
  return typeof error === "object" && error !== null && ("message" in error || "status" in error);
}

/**
 * Singleton component that synchronizes the TanStack Query session state
 * with the XState session actor.
 *
 * This MUST only be rendered once in the app (usually in AppProviders)
 * to prevent infinite reactivity loops.
 */
export function SessionBridge() {
  const snapshot = fromActorRef(sessionActor);

  const query = useQuery(() => ({
    queryKey: queryKeys.auth.session(),
    queryFn: async () => {
      const result = await api.GET("/api/auth/get-session", {
        params: { query: { jwt: true } },
      });
      return result.data ?? null;
    },
    // Only fetch if the machine is done priming (local storage check)
    enabled: !untrack(() => snapshot().value === "priming"),
    staleTime: SESSION_STALE_TIME_MS,
  }));

  createEffect(() => {
    // 1. Only sync if the query has finished its first load
    if (!query.isPending) {
      // Multi-account: honor an explicit GUEST choice. The backend cookie may
      // still authenticate a real account, but reviving that session here would
      // flip the app back to the real identity (and swap the active PowerSync DB
      // away from the guest). The real session is re-primed on switching back.
      if (readActiveAccountSync()?.kind === "guest") {
        return;
      }
      // P23/I1: `data` is the only field that SUSPENDS, and this component lives
      // above `<RouterProvider>` — reading it without a guard crashed the entire
      // app against `__root`'s `<Suspense fallback={null}>`.
      //
      // ⚠️ And the guard also applies to the SEND. This is a DELIBERATE early return:
      // it reproduces the abort that solid-query's proxy performed when reading `.data`
      // on error (the resource suspended and the effect never sent anything).
      // Without it, an unresponsive server would send
      // `SERVER_REPLY(session: null)` and erase the user's primed account.
      // `runSessionCheck` in `beforeLoad` is what confirms guest mode.
      // See `stockpilot-app` `b7e8c48` and `e2e/profile-session-display.spec.ts`.
      if (!query.isSuccess) {
        return;
      }
      const sessionData = query.data ? cloneSession(query.data) : null;

      // 2. Prevent unnecessary updates using robust manual comparison.
      // Use untrack to avoid dependency on the machine state while reading it.
      const current = untrack(() => snapshot().context.session);
      const currentErr = untrack(() => {
        const err = snapshot().context.error;
        return isAppError(err) ? err : null;
      });

      const isDataSame =
        (!sessionData && !current) || (sessionData && sessionData.user.id === current?.user.id);

      // After the `isSuccess` guard, the query error is ALWAYS `null`: the only
      // remaining check is that the machine does not carry an old one
      // (if it does, we must resend to clear it).
      const isErrorSame = !currentErr;

      if (isDataSame && isErrorSame) {
        return;
      }

      console.log("[SessionBridge] Syncing session to machine:", {
        hasData: Boolean(sessionData),
        hasError: Boolean(query.error),
      });

      sessionActor.send({
        type: "SERVER_REPLY",
        session: sessionData,
        error: query.error,
      });
    }
  });

  return null;
}
